import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AlertCircle, CheckCircle, Info } from "lucide-react";
import { useToastStore } from "store/toastStore";

const Toast = () => {
  const { message, type, hideToast } = useToastStore();

  // Local copy so the toast can animate OUT after the store clears the
  // message, instead of unmounting instantly.
  const [current, setCurrent] = useState<{
    message: string;
    type: "success" | "error" | "info" | null;
  } | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (message) {
      setCurrent({ message, type });
      // Start off-screen, then flip to visible on the next frame so the
      // transition runs (slide/fade in).
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    // Message cleared -> animate out; `current` is dropped on transition end.
    setVisible(false);
  }, [message, type]);

  if (!current) return null;

  const toastStyles = {
    success: "bg-gradient-to-r from-green-500 to-green-600 text-white",
    error: "bg-gradient-to-r from-red-500 to-red-600 text-white",
    info: "bg-gradient-to-r from-blue-500 to-blue-600 text-white",
  };

  const iconContainerStyles = {
    success: "bg-green-600",
    error: "bg-red-600",
    info: "bg-blue-600",
  };

  const icons = {
    success: <CheckCircle className="w-5 h-5" />,
    error: <AlertCircle className="w-5 h-5" />,
    info: <Info className="w-5 h-5" />,
  };

  const toastType = current.type ?? "info";

  return createPortal(
    // top-24/right-6 alone landed the toast right on top of the compact
    // mobile header (logo/heart/bell/avatar icons) since it doesn't scale
    // with viewport width or account for the giveaway banner's height above
    // it. Below sm, it's centered with equal left/right insets under the
    // banner+header instead, so it can't overlap either; sm and up keep the
    // original top-right corner placement.
    <div
      className="fixed inset-x-4 flex justify-center sm:inset-x-auto sm:right-6 sm:block"
      style={{
        top: "calc(var(--giveaway-banner-h, 0px) + 4.5rem)",
        zIndex: 99999,
      }}
    >
      <div
        onTransitionEnd={() => {
          // Once the slide-out finishes, stop rendering the toast.
          if (!visible) setCurrent(null);
        }}
        className={`inline-flex items-center overflow-hidden rounded-lg shadow-2xl transform transition-all duration-300 ease-in-out ${
          visible
            ? "translate-x-0 opacity-100"
            : "translate-x-[120%] opacity-0"
        } ${toastStyles[toastType]}`}
        style={{ maxWidth: "min(400px, calc(100vw - 2rem))" }}
      >
        <div
          className={`flex items-center justify-center p-4 ${iconContainerStyles[toastType]}`}
        >
          {icons[toastType]}
        </div>
        <div className="flex-1 p-4">
          <p className="font-medium">{current.message}</p>
        </div>
        <button
          onClick={hideToast}
          className="p-4 hover:bg-black hover:bg-opacity-10 transition-colors duration-200 focus:outline-none"
          aria-label="Close"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default Toast;
